import { ReactNode, useEffect } from "react";
import { Loader } from "lucide-react";
import { useTenant } from "@/contexts/TenantContext";
import { useAuthStore } from "@/stores/useAuthStore";
import { useContactProfile, useHasMissingContact } from "@/hooks/auth/useAuth";
import { firebaseService } from "@/lib/firebase";
import { savedPropertyService } from "@/api/savedProperties";
import { logger } from "@/utils/logger";

// Delay before auto-logging-out a Firebase user that has no matching contact,
// so the "missing contact" message is readable first.
const MISSING_CONTACT_LOGOUT_MS = 5000;

/**
 * Owns the single app-lifetime Firebase auth subscription and drives the auth
 * store. Mounted once, inside the tenant provider so `config` is available.
 * Replaces the old effect-tangled AuthProvider; profile resolution now lives in
 * the useContactProfile React Query hook, not an effect.
 */
export const AuthInitializer = ({ children }: { children: ReactNode }) => {
  const { config } = useTenant();
  const isInitializing = useAuthStore((s) => s.isInitializing);
  const pendingPropertySave = useAuthStore((s) => s.pendingPropertySave);

  // Keeps the profile query active app-wide (guards/pages read the same query).
  const { data: profile } = useContactProfile();
  const user = profile?.user ?? null;
  const hasMissingContact = useHasMissingContact();

  // The single onAuthStateChanged subscription, wired exactly once. This is the
  // one effect that genuinely belongs in an effect — subscribing to an external
  // source. The firebase wrapper normalizes to { uid, email, displayName } | null.
  useEffect(() => {
    const { setFirebaseUser, setInitialized } = useAuthStore.getState();
    const unsubscribe = firebaseService.auth.onAuthStateChanged((identity) => {
      setFirebaseUser(identity);
      setInitialized();
    });
    return unsubscribe;
  }, []);

  // Cross-tab auth sync. Firebase persists the signed-in user to localStorage
  // (browserLocalPersistence), but its own cross-tab propagation doesn't reliably
  // drive React here, so a tab left open stays logged-out until a manual refresh.
  // `storage` fires only in OTHER tabs (never the tab that wrote it), so reading
  // the identity straight off the persisted value keeps every open tab in sync
  // without the self-delivery loop a BroadcastChannel would introduce.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (!event.key || !event.key.startsWith("firebase:authUser:")) return;
      const { firebaseUser, setFirebaseUser } = useAuthStore.getState();

      if (!event.newValue) {
        if (firebaseUser) setFirebaseUser(null);
        return;
      }

      try {
        const persisted = JSON.parse(event.newValue);
        if (!persisted?.uid || persisted.uid === firebaseUser?.uid) return;
        setFirebaseUser({
          uid: persisted.uid,
          email: persisted.email ?? null,
          displayName: persisted.displayName ?? null,
        });
      } catch {
        // Ignore malformed values; the onAuthStateChanged subscription remains
        // the source of truth for this tab's own sign-in/out.
      }
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Save a property that was queued before login, once the profile resolves.
  useEffect(() => {
    if (!user || !pendingPropertySave || !config?.id) return;
    const property = pendingPropertySave;
    savedPropertyService
      .saveProperty(
        user.id,
        config.id,
        property.id.toString(),
        property.mlsNumber,
        undefined,
        {
          full: property.propertyInfo.address.address,
          city: property.propertyInfo.address.city,
          state: property.propertyInfo.address.state,
          zip: property.propertyInfo.address.zip,
        }
      )
      .then(() => useAuthStore.getState().clearPendingPropertySave())
      .catch((error) =>
        logger.error("Error saving property after login:", error)
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, pendingPropertySave, config?.id]);

  // Auto-logout a Firebase user with no matching contact (after a readable delay).
  useEffect(() => {
    if (!hasMissingContact) return;
    const timer = setTimeout(
      () => useAuthStore.getState().logout(),
      MISSING_CONTACT_LOGOUT_MS
    );
    return () => clearTimeout(timer);
  }, [hasMissingContact]);

  if (isInitializing) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return <>{children}</>;
};

export default AuthInitializer;
